// FederalTemplateChecklist — v3.18.1
//
// Compact vertical version of the v3.17.2 FederalTemplateStatusStrip, designed
// to live inside the Executive Verdict's column 1 (under the pWin ring + AI
// Signal box). The narrative reads: ring → AI verdict → "here's why" checklist.
// This replaces the standalone top-of-dashboard 2x3 grid.
//
// Layout per row: status icon · label · count · arrow→destination. Color-coded
// by state (done/partial/empty). Whole row is clickable.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2, Circle, CircleDot, ArrowUpRight, ClipboardList,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Row({ state, label, value, cta, onClick, testId }) {
  const Icon = state === 'done' ? CheckCircle2 : state === 'partial' ? CircleDot : Circle;
  const stateStyles = {
    done: {
      icon: 'text-emerald-600',
      value: 'text-emerald-700',
      label: 'text-slate-700',
      row: 'hover:bg-emerald-50/60',
    },
    partial: {
      icon: 'text-amber-600',
      value: 'text-amber-700 font-semibold',
      label: 'text-slate-700',
      row: 'hover:bg-amber-50/60',
    },
    empty: {
      icon: 'text-slate-300',
      value: 'text-slate-400',
      label: 'text-slate-600',
      row: 'hover:bg-slate-50',
    },
  }[state];

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'group w-full flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-left',
        stateStyles.row,
      )}
    >
      <Icon className={cn('w-4 h-4 shrink-0', stateStyles.icon)} />
      <span className={cn('text-xs flex-1 truncate', stateStyles.label)}>
        {label}
      </span>
      <span className={cn('text-xs tabular-nums', stateStyles.value)}>
        {value}
      </span>
      <ArrowUpRight
        className="w-3 h-3 text-slate-300 group-hover:text-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        aria-hidden="true"
      />
      {/* Hidden text for accessibility / test selectors */}
      <span className="sr-only">{cta}</span>
    </button>
  );
}

export function FederalTemplateChecklist({ pursuitId, authToken }) {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!pursuitId || !authToken) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${authToken}` };
    const load = async () => {
      try {
        const [reqsR, capR, gapsR, cmR] = await Promise.all([
          fetch(`${API_URL}/api/ai/requirements/${pursuitId}`, { headers }).then(r => r.ok ? r.json() : []),
          fetch(`${API_URL}/api/pursuits/${pursuitId}/capability-matrix`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps`, { headers }).then(r => r.ok ? r.json() : []),
          fetch(`${API_URL}/api/pursuits/${pursuitId}/coverage-map`, { headers }).then(r => r.ok ? r.json() : null),
        ]);
        if (cancelled) return;
        const reqs = Array.isArray(reqsR) ? reqsR : [];
        const lcats = (capR?.entries || []);
        const gaps = Array.isArray(gapsR) ? gapsR : [];
        const scored = reqs.filter(r => [0, 1, 2, 3].includes(r.self_score)).length;
        const taskAreas = new Set(reqs.filter(r => r.task_area).map(r => r.task_area));
        const complianceTotal = cmR?.totals?.compliance_entries ?? 0;
        setItems({
          requirementsCount: reqs.length,
          scored,
          taskAreaCount: taskAreas.size,
          lcatsCount: lcats.length,
          complianceCount: complianceTotal,
          gapsCount: gaps.length,
        });
      } catch (_) { /* silent */ }
    };
    load();
    return () => { cancelled = true; };
  }, [pursuitId, authToken]);

  if (!items) return null;

  const scoredState =
    items.requirementsCount === 0 ? 'empty' :
    items.scored === 0 ? 'empty' :
    items.scored === items.requirementsCount ? 'done' :
    'partial';

  const rows = [
    {
      key: 'reqs',
      state: items.requirementsCount > 0 ? 'done' : 'empty',
      label: 'Requirements extracted',
      value: `${items.requirementsCount}`,
      cta: 'Open Intake',
      path: `/${workspaceId}/pursuit/${pursuitId}/intake`,
    },
    {
      key: 'scored',
      state: scoredState,
      label: 'Self-scored (0–3 rubric)',
      value: `${items.scored} / ${items.requirementsCount || 0}`,
      cta: 'Score rows',
      path: `/${workspaceId}/pursuit/${pursuitId}/intake`,
    },
    {
      key: 'taskAreas',
      state: items.taskAreaCount > 0 ? 'done' : 'empty',
      label: 'Task Areas assigned',
      value: `${items.taskAreaCount}`,
      cta: 'Run Shredder',
      path: `/${workspaceId}/pursuit/${pursuitId}/intake`,
    },
    {
      key: 'lcats',
      state: items.lcatsCount > 0 ? 'done' : 'empty',
      label: 'LCATs mapped',
      value: `${items.lcatsCount}`,
      cta: 'Open Matrix',
      path: `/${workspaceId}/pursuit/${pursuitId}/gaps`,
    },
    {
      key: 'compliance',
      state: items.complianceCount > 0 ? 'done' : 'empty',
      label: 'Compliance Matrix entries',
      value: `${items.complianceCount}`,
      cta: 'Open Tracker',
      path: `/${workspaceId}/pursuit/${pursuitId}/compliance`,
    },
    {
      key: 'gaps',
      state: items.gapsCount > 0 ? 'done' : 'empty',
      label: 'Gap Tracker rows',
      value: `${items.gapsCount}`,
      cta: 'Open Gaps',
      path: `/${workspaceId}/pursuit/${pursuitId}/gaps`,
    },
  ];

  const doneCount = rows.filter(r => r.state === 'done').length;
  const total = rows.length;
  const allDone = doneCount === total;
  const progressPct = Math.round((doneCount / total) * 100);

  // Causal storytelling: header text adapts to the current state.
  const headerLabel = allDone
    ? 'All federal artifacts ready'
    : doneCount === 0
      ? 'Get started — populate these artifacts'
      : 'Why this score may be low';

  const headerTone = allDone
    ? 'text-emerald-700'
    : doneCount === 0
      ? 'text-slate-600'
      : 'text-amber-700';

  return (
    <div
      className="mt-4 w-full border border-slate-200 rounded-md bg-white"
      data-testid="federal-template-checklist"
    >
      {/* Header with progress */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <ClipboardList className={cn('w-3.5 h-3.5', headerTone)} />
        <span className={cn('text-[11px] font-semibold uppercase tracking-wider', headerTone)}>
          {headerLabel}
        </span>
        <span className="text-[11px] text-slate-500 ml-auto tabular-nums" data-testid="federal-checklist-progress-text">
          {doneCount}/{total}
        </span>
      </div>
      {/* Inline progress bar */}
      <div className="h-1 w-full bg-slate-100">
        <div
          className={cn(
            'h-full transition-all',
            allDone ? 'bg-emerald-500' : doneCount === 0 ? 'bg-slate-300' : 'bg-amber-400',
          )}
          style={{ width: `${progressPct}%` }}
          data-testid="federal-checklist-progress-bar"
        />
      </div>
      {/* Rows */}
      <div className="p-1.5 space-y-0.5">
        {rows.map(r => (
          <Row
            key={r.key}
            state={r.state}
            label={r.label}
            value={r.value}
            cta={r.cta}
            onClick={() => navigate(r.path)}
            testId={`federal-checklist-row-${r.key}`}
          />
        ))}
      </div>
    </div>
  );
}
